// Page de vérification d'identité - Design Premium
// Liveness Detection + Capture selfie + pièce d'identité avec IA Claude Vision
// RGPD: Aucune donnée stockée après vérification

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, CreditCard, CheckCircle, XCircle, AlertCircle,
  RefreshCw, ChevronRight, Shield, User, FileCheck,
  Lock, Eye, Trash2, Clock, MessageCircle, Fingerprint,
  Sparkles, ShieldCheck, ArrowRight, ArrowLeft, MoveLeft, 
  MoveRight, Smile, Calendar, Phone
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DOCUMENT_TYPES = [
  { id: 'id_card', label: "Carte d'identité", icon: CreditCard, description: "CNI française ou européenne" },
  { id: 'passport', label: 'Passeport', icon: FileCheck, description: "Passeport valide" },
  { id: 'driver_license', label: 'Permis de conduire', icon: CreditCard, description: "Permis de conduire valide" },
];

// Instructions pour Liveness Detection
const LIVENESS_STEPS = [
  { id: 'center', instruction: "Regarde la caméra", icon: User, duration: 2000 },
  { id: 'left', instruction: "Tourne la tête à gauche", icon: MoveLeft, duration: 2500 },
  { id: 'right', instruction: "Tourne la tête à droite", icon: MoveRight, duration: 2500 },
  { id: 'smile', instruction: "Souris !", icon: Smile, duration: 2000 },
];

// Composant Trust Badge
function TrustBadge({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-gray-100">
      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon size={20} className="text-white" />
      </div>
      <div>
        <p className="font-semibold text-gray-900 text-sm">{title}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
  );
}

// Composant Progress Steps Premium
function ProgressSteps({ current }) {
  const steps = ['Intro', 'Liveness', 'Document', 'Validation'];
  return (
    <div className="flex items-center justify-center gap-1 px-4">
      {steps.map((label, i) => (
        <div key={i} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
              current > i + 1 
                ? 'bg-emerald-500 text-white' 
                : current === i + 1 
                  ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30' 
                  : 'bg-gray-100 text-gray-400'
            }`}>
              {current > i + 1 ? <CheckCircle size={16} /> : i + 1}
            </div>
            <span className={`text-[10px] mt-1 ${current === i + 1 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`w-8 h-0.5 mx-1 mb-4 transition-colors ${current > i + 1 ? 'bg-emerald-500' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// Composant Liveness Instruction
function LivenessInstruction({ step, isActive }) {
  const Icon = step.icon;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: isActive ? 1 : 0.3, scale: isActive ? 1 : 0.9 }}
      className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all ${
        isActive 
          ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg' 
          : 'bg-gray-100 text-gray-400'
      }`}
    >
      <Icon size={32} />
      <span className="text-sm font-semibold">{step.instruction}</span>
    </motion.div>
  );
}

export default function VerifyIdentityPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const documentCameraRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [livenessStep, setLivenessStep] = useState(0);
  const [livenessComplete, setLivenessComplete] = useState(false);
  const [livenessPhotos, setLivenessPhotos] = useState([]);
  const [documentData, setDocumentData] = useState(null);
  const [documentType, setDocumentType] = useState('id_card');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(null);
  
  const token = localStorage.getItem('ecosystem_token');

  // Start camera
  const startCamera = useCallback(async (facingMode = 'user') => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError(true);
        toast.error("Caméra non disponible. Cette vérification nécessite un accès caméra.");
        return false;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
        setCameraError(false);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(true);
      
      if (err.name === 'NotAllowedError') {
        toast.error("Accès caméra refusé. Autorise l'accès dans les paramètres de ton navigateur.");
      } else if (err.name === 'NotFoundError') {
        toast.error("Aucune caméra détectée sur cet appareil.");
      } else {
        toast.error("Impossible d'accéder à la caméra. Réessaie.");
      }
      return false;
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  }, []);

  // Capture photo from video
  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      // Mirror for selfie
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      return dataUrl.split(',')[1];
    }
    return null;
  }, []);

  // Start Liveness Detection
  const startLivenessDetection = async () => {
    const cameraStarted = await startCamera('user');
    if (!cameraStarted) return;
    
    setLivenessStep(0);
    setLivenessPhotos([]);
    setLivenessComplete(false);
    
    // Wait for camera to stabilize
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Run through liveness steps
    for (let i = 0; i < LIVENESS_STEPS.length; i++) {
      setLivenessStep(i);
      setCountdown(3);
      
      // Countdown
      for (let c = 3; c > 0; c--) {
        setCountdown(c);
        await new Promise(resolve => setTimeout(resolve, 800));
      }
      setCountdown(null);
      
      // Capture photo
      const photo = capturePhoto();
      if (photo) {
        setLivenessPhotos(prev => [...prev, { step: LIVENESS_STEPS[i].id, data: photo }]);
      }
      
      // Show success feedback
      toast.success(LIVENESS_STEPS[i].instruction + " ✓", { duration: 1000 });
      
      // Wait before next step
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Liveness complete
    setLivenessComplete(true);
    stopCamera();
    setStep(3); // Move to document type selection
  };

  // Handle document photo from native camera
  const handleDocumentUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.match(/image\/(jpeg|png|webp|heic|heif)/)) {
      toast.error('Format non supporté. Utilise JPEG ou PNG.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      setDocumentData(base64);
      setStep(5); // Move to processing
      submitVerification(base64);
    };
    reader.readAsDataURL(file);
  };

  // Submit verification
  const submitVerification = async (document) => {
    setLoading(true);
    
    // Get the best selfie from liveness photos (the smile one or last one)
    const selfiePhoto = livenessPhotos.find(p => p.step === 'smile') || livenessPhotos[livenessPhotos.length - 1];
    
    if (!selfiePhoto) {
      toast.error("Erreur: Selfie manquant");
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/verification/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          selfie_base64: selfiePhoto.data,
          id_document_base64: document,
          id_document_type: documentType,
          liveness_photos: livenessPhotos.map(p => ({ step: p.step, data: p.data }))
        })
      });
      
      const data = await res.json();
      setResult(data);
      setStep(6);
      
      if (data.status === 'verified') {
        const profile = JSON.parse(localStorage.getItem('ecosystem_profile') || '{}');
        profile.is_verified = true;
        profile.identity_status = 'verified';
        localStorage.setItem('ecosystem_profile', JSON.stringify(profile));
      }
    } catch (err) {
      setResult({ status: 'error', message: 'Erreur de connexion' });
      setStep(6);
    } finally {
      setLoading(false);
    }
  };

  // Reset
  const reset = () => {
    setStep(1);
    setLivenessStep(0);
    setLivenessComplete(false);
    setLivenessPhotos([]);
    setDocumentData(null);
    setResult(null);
    setCameraActive(false);
    setCameraError(false);
    setCountdown(null);
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-blue-600" />
            <span className="font-semibold text-gray-900">Vérification ID</span>
          </div>
          <div className="w-10" />
        </div>
        
        {/* Progress */}
        <div className="pb-4">
          <ProgressSteps current={step} />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-6 pb-32">
        <AnimatePresence mode="wait">
          
          {/* ═══ ÉTAPE 1: INTRO ═══ */}
          {step === 1 && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-5"
            >
              {/* Hero */}
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl shadow-blue-500/30">
                  <ShieldCheck size={40} className="text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Rejoins le réseau de confiance</h1>
                <p className="text-gray-500 mt-2">Vérifie ton identité en 2 minutes</p>
              </div>

              {/* Stats */}
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Shield size={24} />
                  <h3 className="font-semibold">Notre objectif</h3>
                </div>
                <p className="text-white/80 text-sm mb-4">
                  Créer une <strong>communauté de personnes de confiance</strong> où chacun peut interagir, acheter et vendre en toute sérénité.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold">1 = 1</p>
                    <p className="text-xs text-white/70">Une personne = Un compte</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold">0</p>
                    <p className="text-xs text-white/70">Faux compte toléré</p>
                  </div>
                </div>
              </div>

              {/* Process */}
              <div className="space-y-3">
                <TrustBadge 
                  icon={User} 
                  title="1. Vérification en direct" 
                  description="Tu bouges la tête devant la caméra pour prouver que c'est toi"
                />
                <TrustBadge 
                  icon={CreditCard} 
                  title="2. Photo de ta pièce d'identité" 
                  description="CNI, passeport ou permis de conduire"
                />
                <TrustBadge 
                  icon={Sparkles} 
                  title="3. Analyse IA instantanée" 
                  description="Vérification en quelques secondes"
                />
              </div>

              {/* RGPD */}
              <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                    <Lock size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800 text-sm">100% RGPD</p>
                    <p className="text-xs text-emerald-600">Aucune photo stockée après vérification</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Button
                onClick={() => setStep(2)}
                className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-semibold text-base shadow-lg shadow-blue-500/30"
              >
                Commencer la vérification
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </motion.div>
          )}

          {/* ═══ ÉTAPE 2: LIVENESS DETECTION ═══ */}
          {step === 2 && (
            <motion.div
              key="liveness"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/20">
                  <User size={28} className="text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Vérification en direct</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Suis les instructions pour prouver que c'est bien toi
                </p>
              </div>

              {/* Camera zone */}
              <div className="relative bg-gray-900 rounded-2xl overflow-hidden aspect-[3/4] shadow-xl">
                {cameraActive ? (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover scale-x-[-1]"
                    />
                    
                    {/* Countdown overlay */}
                    {countdown && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <motion.div
                          key={countdown}
                          initial={{ scale: 0.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="text-8xl font-bold text-white drop-shadow-lg"
                        >
                          {countdown}
                        </motion.div>
                      </div>
                    )}
                    
                    {/* Current instruction */}
                    {!countdown && LIVENESS_STEPS[livenessStep] && (
                      <div className="absolute bottom-4 left-4 right-4">
                        <motion.div
                          key={livenessStep}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-4 text-white text-center"
                        >
                          <div className="flex items-center justify-center gap-2 mb-1">
                            {(() => {
                              const Icon = LIVENESS_STEPS[livenessStep].icon;
                              return <Icon size={24} />;
                            })()}
                          </div>
                          <p className="font-semibold">{LIVENESS_STEPS[livenessStep].instruction}</p>
                        </motion.div>
                      </div>
                    )}
                    
                    {/* Face guide overlay */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-60 border-2 border-white/40 rounded-[100px] border-dashed" />
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                    <Camera size={48} className="mb-3 opacity-50" />
                    <p className="text-sm">{cameraError ? "Caméra non accessible" : "Caméra prête"}</p>
                    {cameraError && (
                      <p className="text-xs text-gray-500 mt-2 px-4 text-center">
                        Autorise l'accès caméra pour continuer
                      </p>
                    )}
                  </div>
                )}
                
                <canvas ref={canvasRef} className="hidden" />
              </div>

              {/* Instructions preview */}
              <div className="grid grid-cols-4 gap-2">
                {LIVENESS_STEPS.map((s, i) => {
                  const Icon = s.icon;
                  const isComplete = livenessPhotos.some(p => p.step === s.id);
                  const isCurrent = cameraActive && livenessStep === i;
                  return (
                    <div
                      key={s.id}
                      className={`flex flex-col items-center p-2 rounded-xl text-center transition-all ${
                        isComplete
                          ? 'bg-emerald-100 text-emerald-700'
                          : isCurrent
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {isComplete ? <CheckCircle size={20} /> : <Icon size={20} />}
                      <span className="text-[10px] mt-1 font-medium">{s.instruction.split(' ')[0]}</span>
                    </div>
                  );
                })}
              </div>

              {/* Security info */}
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                <p className="text-xs text-amber-700 text-center flex items-center justify-center gap-2">
                  <Shield size={14} />
                  Vérification en direct obligatoire pour ta sécurité
                </p>
              </div>

              {/* Action button */}
              {!cameraActive && !livenessComplete && (
                <Button
                  onClick={startLivenessDetection}
                  disabled={cameraError}
                  className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-semibold shadow-lg shadow-blue-500/30"
                >
                  <Camera size={20} className="mr-2" />
                  Démarrer la vérification
                </Button>
              )}
            </motion.div>
          )}

          {/* ═══ ÉTAPE 3: TYPE DE DOCUMENT ═══ */}
          {step === 3 && (
            <motion.div
              key="doctype"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-indigo-500/20">
                  <CreditCard size={28} className="text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Choisis ton document</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Sélectionne le type de pièce d'identité
                </p>
              </div>

              {/* Liveness validated */}
              <div className="bg-emerald-50 rounded-xl p-3 flex items-center gap-3 border border-emerald-100">
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-emerald-800 text-sm">Vérification en direct réussie</p>
                  <p className="text-xs text-emerald-600">{livenessPhotos.length} photos capturées</p>
                </div>
              </div>

              {/* Document types */}
              <div className="space-y-3">
                {DOCUMENT_TYPES.map((doc) => {
                  const Icon = doc.icon;
                  const isSelected = documentType === doc.id;
                  return (
                    <button
                      key={doc.id}
                      onClick={() => setDocumentType(doc.id)}
                      className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-blue-500' : 'bg-gray-100'
                      }`}>
                        <Icon size={24} className={isSelected ? 'text-white' : 'text-gray-500'} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                          {doc.label}
                        </p>
                        <p className="text-xs text-gray-500">{doc.description}</p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                      }`}>
                        {isSelected && <CheckCircle size={14} className="text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={() => setStep(4)}
                className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-semibold shadow-lg shadow-blue-500/30"
              >
                Continuer
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </motion.div>
          )}

          {/* ═══ ÉTAPE 4: PHOTO DOCUMENT ═══ */}
          {step === 4 && (
            <motion.div
              key="document"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-purple-500/20">
                  <FileCheck size={28} className="text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Photo de ta {DOCUMENT_TYPES.find(d => d.id === documentType)?.label}
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Prends en photo le recto de ton document
                </p>
              </div>

              {/* Document preview zone */}
              <div className="relative bg-gray-900 rounded-2xl overflow-hidden aspect-[4/3] shadow-xl">
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                  <CreditCard size={48} className="mb-3 opacity-50" />
                  <p className="text-sm">Prêt à photographier</p>
                </div>
                
                {/* Document guide overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-6">
                  <div className="w-full h-full border-2 border-white/40 rounded-xl border-dashed" />
                </div>
              </div>

              {/* Conseils */}
              <div className="bg-purple-50 rounded-xl p-3 border border-purple-100">
                <p className="text-xs text-purple-800 text-center">
                  Document bien cadré • Toutes les infos lisibles • Évite les reflets
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={() => documentCameraRef.current?.click()}
                  className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-2xl font-semibold shadow-lg shadow-purple-500/30 flex items-center justify-center gap-2"
                >
                  <Camera size={20} />
                  Photographier le document
                </button>
                
                {/* Security info */}
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                  <p className="text-xs text-amber-700 text-center flex items-center justify-center gap-2">
                    <Shield size={14} />
                    Photo en direct uniquement - Import galerie non autorisé
                  </p>
                </div>
              </div>
              
              {/* Input caméra native (environment = caméra arrière pour documents) */}
              <input
                ref={documentCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleDocumentUpload}
                className="hidden"
              />
            </motion.div>
          )}

          {/* ═══ ÉTAPE 5: ANALYSE EN COURS ═══ */}
          {step === 5 && (
            <motion.div
              key="processing"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-xl">
                  <Sparkles size={40} className="text-white" />
                </div>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-2 border-4 border-blue-200 border-t-blue-500 rounded-full"
                />
              </div>
              
              <h2 className="text-xl font-bold text-gray-900 mt-8">Analyse en cours...</h2>
              <p className="text-gray-500 text-sm mt-2 text-center">
                Notre IA vérifie ton identité
              </p>
              
              <div className="mt-8 space-y-2 w-full max-w-xs">
                {[
                  "Analyse du selfie...",
                  "Vérification du document...",
                  "Comparaison des visages...",
                  "Calcul du score de confiance..."
                ].map((text, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.5 }}
                    className="flex items-center gap-2 text-sm text-gray-600"
                  >
                    <CheckCircle size={16} className="text-emerald-500" />
                    {text}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ═══ ÉTAPE 6: RÉSULTAT ═══ */}
          {step === 6 && result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-10"
            >
              {result.status === 'verified' && (
                <>
                  <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-xl shadow-emerald-500/30">
                    <CheckCircle size={48} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mt-6">Identité vérifiée !</h2>
                  <p className="text-gray-500 mt-2">
                    Tu fais maintenant partie du réseau de confiance
                  </p>
                  {result.confidence && (
                    <div className="mt-4 inline-block bg-emerald-100 px-4 py-2 rounded-full">
                      <span className="text-emerald-700 font-semibold">
                        Score de confiance: {result.confidence}%
                      </span>
                    </div>
                  )}
                  <Button
                    onClick={() => navigate('/cockpit-particulier')}
                    className="w-full h-14 mt-8 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-2xl font-semibold shadow-lg"
                  >
                    Accéder à mon cockpit
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </>
              )}

              {result.status === 'manual_review' && (
                <>
                  <div className="w-24 h-24 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto shadow-xl shadow-amber-500/30">
                    <Clock size={48} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mt-6">Vérification en cours</h2>
                  <p className="text-gray-500 mt-2">
                    Ta vérification nécessite un contrôle supplémentaire
                  </p>
                  
                  {result.requires_appointment && (
                    <div className="mt-6 bg-blue-50 rounded-2xl p-4 border border-blue-100">
                      <div className="flex items-center gap-3 justify-center mb-3">
                        <Calendar size={24} className="text-blue-600" />
                        <span className="font-semibold text-blue-800">Prends rendez-vous</span>
                      </div>
                      <p className="text-sm text-blue-700 mb-4">
                        Un appel vidéo de 1 minute avec notre équipe pour finaliser ta vérification
                      </p>
                      <Button
                        onClick={() => navigate('/verification-rdv')}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                      >
                        <Phone size={18} className="mr-2" />
                        Choisir un créneau
                      </Button>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => navigate('/cockpit-particulier')}
                    variant="outline"
                    className="w-full h-12 mt-4 rounded-2xl"
                  >
                    Retour au cockpit
                  </Button>
                </>
              )}

              {result.status === 'rejected' && (
                <>
                  <div className="w-24 h-24 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto shadow-xl shadow-red-500/30">
                    <XCircle size={48} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mt-6">Vérification échouée</h2>
                  <p className="text-gray-500 mt-2">
                    {result.message || "La vérification n'a pas pu être validée"}
                  </p>
                  <div className="flex gap-3 mt-8">
                    <Button
                      onClick={reset}
                      className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl"
                    >
                      <RefreshCw size={18} className="mr-2" />
                      Réessayer
                    </Button>
                    <Button
                      onClick={() => navigate('/support')}
                      variant="outline"
                      className="flex-1 h-12 rounded-2xl"
                    >
                      Contacter le support
                    </Button>
                  </div>
                </>
              )}

              {result.status === 'error' && (
                <>
                  <div className="w-24 h-24 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center mx-auto shadow-xl">
                    <AlertCircle size={48} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mt-6">Erreur technique</h2>
                  <p className="text-gray-500 mt-2">{result.message}</p>
                  <Button
                    onClick={reset}
                    className="w-full h-14 mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-semibold"
                  >
                    <RefreshCw size={18} className="mr-2" />
                    Réessayer
                  </Button>
                </>
              )}
            </motion.div>
          )}
          
        </AnimatePresence>
      </div>
    </div>
  );
}
